import { useEffect, useState } from "react";
import apiClient from "../api/client";
import { Workspace } from "../types";

export interface WorkspaceListState {
  workspaces: Workspace[];
  loading: boolean;
  error: string | null;
  /** Personal ("My workspace") workspaces are excluded server-side by default. */
  showPersonal: boolean;
  setShowPersonal: (show: boolean) => void;
  refetch: () => void;
}

/**
 * Shared workspace list for filter dropdowns.
 *
 * The API excludes personal workspaces unless include_personal=true, so every
 * consumer is personal-free by default; flipping showPersonal refetches with
 * them included. Pass { monitoredOnly: true } on SOX-control pages so the
 * dropdown only offers workspaces in the audit scope.
 */
export default function useWorkspaceList(
  opts: { monitoredOnly?: boolean } = {}
): WorkspaceListState {
  const { monitoredOnly = false } = opts;
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPersonal, setShowPersonal] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params: Record<string, string> = {};
    if (showPersonal) params.include_personal = "true";
    if (monitoredOnly) params.monitored_only = "true";
    apiClient
      .get<Workspace[]>("/api/workspaces", { params })
      .then((res) => { if (!cancelled) setWorkspaces(res.data); })
      .catch((err) => { if (!cancelled) setError(err?.message ?? "Failed to load workspaces."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [showPersonal, monitoredOnly, reloadKey]);

  return {
    workspaces,
    loading,
    error,
    showPersonal,
    setShowPersonal,
    refetch: () => setReloadKey((k) => k + 1),
  };
}
